import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { NgxUiLoaderService } from 'ngx-ui-loader';

import { MyShipPopupComponent } from '../my-ship-popup/my-ship-popup.component';
import { EmissionService } from '../../service/emission-service/emission.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-generate-emission-report',
  templateUrl: './generate-emission-report.component.html',
  styleUrls: ['./generate-emission-report.component.scss'],
})
export class GenerateEmissionReportComponent implements OnInit {
  myshipForm: FormGroup;
  isSubmitting = false;

  imoNumber: any;
  reportType: 'CII' | 'IMO-DCS' | 'FUELEU' | 'EUETS' | 'UKMRV' = 'CII';

  operatorlistOptions = [
    { label: 'SAKA - Satish', value: 'SAKA' },
    { label: 'SABA - Sandokan', value: 'SABA' },
    { label: 'ROAK - Roman', value: 'ROAK' },
    { label: 'AAHA - Ahmet', value: 'AAHA' },
    { label: 'NIAL - Nikita', value: 'NIAL' },
    { label: 'RAPA - Rahul', value: 'RAPA' },
    { label: 'AHKM - Abhishek', value: 'AHKM' },
    { label: 'SAMH - Sadique Muhammad', value: 'SAMH' },
    { label: 'ALYI - Alex', value: 'ALYI' },
    { label: 'KHGL - Khazar Guliyev', value: 'KHGL' },
  ];

  minEndDate: string = '';


  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private emissionService: EmissionService,
    public dialogRef: MatDialogRef<GenerateEmissionReportComponent>,
    private cdr: ChangeDetectorRef,
    private loader: NgxUiLoaderService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.imoNumber = this.data?.ship_imo || this.data?.imo || this.data?.vesselIMO || this.data?.imo_number || NaN;
    this.reportType = this.data?.reportType || 'CII';

    this.myshipForm = this.fb.group({
      fromDate: ['', Validators.required],
      fromTime: ['00:00', Validators.required],
      toDate: ['', Validators.required],
      toTime: ['23:59', Validators.required],
    });

    this.myshipForm.get('fromDate')?.valueChanges.subscribe((date) => {
      this.minEndDate = date || '';
      // If toDate is before new minEndDate, clear it
      const toDate = this.myshipForm.get('toDate')?.value;
      if (toDate && date && toDate < date) {
        this.myshipForm.get('toDate')?.patchValue('');
      }
    });

  }

  ngOnInit(): void {
  }

  quickSelect(period: string) {
    const today = new Date();
    const year = today.getFullYear();
    let start = `${year}-01-01`;
    let end = '';

    switch (period) {
      case 'Q1':
        start = `${year}-01-01`;
        end = `${year}-03-31`;
        break;
      case 'Q2':
        start = `${year}-04-01`;
        end = `${year}-06-30`;
        break;
      case 'Q3':
        start = `${year}-07-01`;
        end = `${year}-09-30`;
        break;
      case 'Q4':
        start = `${year}-10-01`;
        end = `${year}-12-31`;
        break;
      case 'Year':
        start = `${year}-01-01`;
        end = `${year}-12-31`;
        break;
      case 'YTD':
        start = `${year}-01-01`;
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        end = `${year}-${month}-${day}`;
        break;
    }
    this.myshipForm.patchValue({
      fromDate: start,
      fromTime: '00:00',
      toDate: end,
      toTime: '23:59',
    });
  }



  isProcessing = false;
  hasError = false;
  errorMessage = '';

  submit() {
    this.myshipForm.markAllAsTouched();
    if (this.myshipForm.invalid) {
      this.toastr.warning('Please select a valid date and time range');
      return;
    }

    const { fromDate, fromTime, toDate, toTime } = this.myshipForm.getRawValue();
    const startTime = fromTime || '00:00';
    const endTime = toTime || '23:59';

    const startIso = `${fromDate}T${startTime}:00Z`;
    const endIso = `${toDate}T${endTime}:${endTime === '23:59' ? '59' : '00'}Z`;

    if (new Date(startIso) >= new Date(endIso)) {
      this.toastr.warning('End date and time must be after start date and time');
      return;
    }

    this.isProcessing = true;
    this.isSubmitting = true;
    this.hasError = false;
    this.errorMessage = '';
    this.cdr.detectChanges();

    this.loader.stopAll();

    const payload = {
      start_time: startIso,
      end_time: endIso,
    };

    let reportObservable: Observable<any>;

    switch (this.reportType) {
      case 'IMO-DCS':
        reportObservable = this.emissionService.getImoDcsDetailedReport(this.imoNumber, payload);
        break;
      case 'FUELEU':
        reportObservable = this.emissionService.getFuelEuReport(this.imoNumber, payload);
        break;
      case 'EUETS':
        reportObservable = this.emissionService.getEuEtsReport(this.imoNumber, payload);
        break;
      case 'UKMRV':
        reportObservable = this.emissionService.getUkMrvReport(this.imoNumber, payload);
        break;
      default:
        reportObservable = this.emissionService.getCiiReport(this.imoNumber, payload);
    }

    // Subscribing directly to handle modal state
    reportObservable.subscribe({
      next: (res: any) => {
        this.isSubmitting = false;

        if (res?.success === true) {
          // 1. Close current modal on success
          this.closeModal();

          // 2. Open results modal using the existing data (avoids redundant call)
          this.emissionService.showReportResults(res, this.reportType);
        } else {
          this.isProcessing = false;
          this.hasError = true;
          this.errorMessage = res?.message || res?.error || 'Report generation failed. Please check the date range or try again later.';
          this.cdr.detectChanges();
        }
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.isProcessing = false;
        this.hasError = true;
        this.errorMessage = err?.error?.error || err?.error?.message || 'An error occurred while generating the report. Please try again.';
        this.cdr.detectChanges();
        console.error('Report Error:', err);
      }
    });
  }

  retry() {
    this.hasError = false;
    this.isProcessing = false;
    this.isSubmitting = false;
    this.errorMessage = '';
    this.cdr.detectChanges();
  }

  // Close popup
  closeModal() {
    this.dialogRef.close();
  }
  openOrDownload(url: string) {
    if (!url) return;

    setTimeout(() => {
      window.open(url, '_blank');
    }, 300);
  }



}
